import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { analyticsAPI, streakAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { SUBJECTS, SUBJECT_COLORS, formatPercentage } from '../lib/utils';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import {
    BookOpen,
    Target,
    TrendingUp,
    AlertTriangle,
    Clock,
    CheckCircle,
    XCircle,
    ArrowRight,
    Zap,
    RefreshCw,
    Flame,
    Trophy
} from 'lucide-react';
import Layout from '../components/Layout';

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [streak, setStreak] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchStats();
        fetchStreak();
    }, []);

    const fetchStats = async () => {
        try {
            setLoading(true);
            const response = await analyticsAPI.getPerformance();
            setStats(response.data);
        } catch (err) {
            console.error('Failed to fetch stats:', err);
            setError('Failed to load analytics');
        } finally {
            setLoading(false);
        }
    };

    const fetchStreak = async () => {
        try {
            const response = await streakAPI.get();
            setStreak(response.data);
        } catch (err) {
            console.error('Failed to fetch streak:', err);
        }
    };

    const subjectChartData = stats ? SUBJECTS.map(subject => ({
        name: subject.substring(0, 4),
        fullName: subject,
        accuracy: stats.by_subject[subject]?.accuracy || 0,
        total: stats.by_subject[subject]?.total || 0
    })) : [];

    const trendChartData = stats?.recent_trend || [];

    const difficultyData = stats ? [
        { name: 'Easy', value: stats.by_difficulty.easy?.total || 0, color: '#10B981' },
        { name: 'Medium', value: stats.by_difficulty.medium?.total || 0, color: '#F59E0B' },
        { name: 'Hard', value: stats.by_difficulty.hard?.total || 0, color: '#EF4444' }
    ].filter(d => d.value > 0) : [];

    if (loading) {
        return (
            <Layout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[1, 2, 3, 4].map(i => (
                            <Skeleton key={i} className="h-32" />
                        ))}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Skeleton className="h-80" />
                        <Skeleton className="h-80" />
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="dashboard-container">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                            Welcome back, {user?.name?.split(' ')[0]}!
                        </h1>
                        <p className="text-slate-500 mt-1">Here's your learning progress overview</p>
                    </div>
                    <Button onClick={() => navigate('/practice')} data-testid="start-practice-btn">
                        <Zap className="mr-2 h-4 w-4" />
                        Start Practice
                    </Button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card className="border-slate-200 hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">Total Questions</p>
                                    <p className="text-3xl font-bold text-slate-900" data-testid="total-questions">
                                        {stats?.total_questions || 0}
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                                    <BookOpen className="h-6 w-6 text-indigo-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-slate-200 hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">Accuracy</p>
                                    <p className="text-3xl font-bold text-slate-900" data-testid="accuracy-percentage">
                                        {formatPercentage(stats?.accuracy_percentage || 0)}
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                                    <Target className="h-6 w-6 text-emerald-600" />
                                </div>
                            </div>
                            <Progress 
                                value={stats?.accuracy_percentage || 0} 
                                className="mt-4 h-2"
                            />
                        </CardContent>
                    </Card>

                    <Card className="border-slate-200 hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">Correct / Incorrect</p>
                                    <p className="text-3xl font-bold text-slate-900">
                                        <span className="text-emerald-600" data-testid="correct-count">{stats?.correct_answers || 0}</span>
                                        <span className="text-slate-300 mx-2">/</span>
                                        <span className="text-red-500" data-testid="incorrect-count">{stats?.incorrect_answers || 0}</span>
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center">
                                    <TrendingUp className="h-6 w-6 text-amber-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-slate-200 hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">Due for Review</p>
                                    <p className="text-3xl font-bold text-slate-900" data-testid="review-due-count">
                                        {stats?.spaced_repetition_due || 0}
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center">
                                    <RefreshCw className="h-6 w-6 text-purple-600" />
                                </div>
                            </div>
                            {stats?.spaced_repetition_due > 0 && (
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="mt-3 w-full text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                    onClick={() => navigate('/review')}
                                    data-testid="review-now-btn"
                                >
                                    Review Now <ArrowRight className="ml-1 h-4 w-4" />
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Study Streak Widget */}
                {streak && (
                    <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="study-streak-widget">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${streak.current_streak > 0 ? 'bg-orange-100' : 'bg-slate-100'}`}>
                                        <Flame className={`h-6 w-6 ${streak.current_streak > 0 ? 'text-orange-500' : 'text-slate-400'}`} />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">Study Streak</p>
                                        <p className="text-3xl font-bold text-slate-900" data-testid="streak-count">
                                            {streak.current_streak} <span className="text-base font-normal text-slate-500">day{streak.current_streak !== 1 ? 's' : ''}</span>
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="flex items-center gap-1.5 text-sm text-slate-500">
                                        <Trophy className="h-4 w-4 text-amber-500" />
                                        Best: <span className="font-semibold text-slate-700" data-testid="streak-longest">{streak.longest_streak}</span> days
                                    </div>
                                    <p className="text-xs text-slate-400 mt-1">{streak.total_active_days} active days (90d)</p>
                                </div>
                            </div>
                            {/* Activity heatmap - last 30 days */}
                            <div className="flex flex-wrap gap-1" data-testid="streak-heatmap">
                                {Object.entries(streak.activity_map)
                                    .sort(([a], [b]) => a.localeCompare(b))
                                    .map(([date, active]) => (
                                        <div
                                            key={date}
                                            title={`${date}: ${active ? 'Active' : 'Inactive'}`}
                                            className={`w-3 h-3 rounded-sm ${active ? 'bg-emerald-500' : 'bg-slate-100'}`}
                                        />
                                    ))}
                            </div>
                            {streak.milestone && (
                                <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="streak-milestone">
                                    {streak.milestone}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Subject Performance */}
                    <Card className="border-slate-200">
                        <CardHeader>
                            <CardTitle style={{ fontFamily: 'Manrope' }}>Performance by Subject</CardTitle>
                            <CardDescription>Your accuracy across different subjects</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {subjectChartData.some(d => d.total > 0) ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart data={subjectChartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                        <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                                        <Tooltip 
                                            content={({ active, payload }) => {
                                                if (active && payload && payload.length) {
                                                    const data = payload[0].payload;
                                                    return (
                                                        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
                                                            <p className="font-medium">{data.fullName}</p>
                                                            <p className="text-sm text-slate-600">
                                                                Accuracy: {data.accuracy}%
                                                            </p>
                                                            <p className="text-sm text-slate-600">
                                                                Questions: {data.total}
                                                            </p>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <Bar 
                                            dataKey="accuracy" 
                                            fill="#4F46E5" 
                                            radius={[4, 4, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-[250px] flex items-center justify-center text-slate-500">
                                    <div className="text-center">
                                        <BookOpen className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                                        <p>No data yet. Start practicing!</p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* 7-Day Trend */}
                    <Card className="border-slate-200">
                        <CardHeader>
                            <CardTitle style={{ fontFamily: 'Manrope' }}>7-Day Trend</CardTitle>
                            <CardDescription>Your recent activity and performance</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {trendChartData.some(d => d.total > 0) ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart data={trendChartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                        <XAxis 
                                            dataKey="date" 
                                            tick={{ fontSize: 12 }}
                                            tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { weekday: 'short' })}
                                        />
                                        <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                                        <Tooltip 
                                            content={({ active, payload }) => {
                                                if (active && payload && payload.length) {
                                                    const data = payload[0].payload;
                                                    return (
                                                        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
                                                            <p className="font-medium">{new Date(data.date).toLocaleDateString()}</p>
                                                            <p className="text-sm text-slate-600">
                                                                Questions: {data.total}
                                                            </p>
                                                            <p className="text-sm text-emerald-600">
                                                                Accuracy: {data.accuracy}%
                                                            </p>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <Line 
                                            type="monotone" 
                                            dataKey="accuracy" 
                                            stroke="#10B981" 
                                            strokeWidth={2}
                                            dot={{ fill: '#10B981', strokeWidth: 2 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-[250px] flex items-center justify-center text-slate-500">
                                    <div className="text-center">
                                        <TrendingUp className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                                        <p>No activity in the last 7 days</p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Bottom Row */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Weak Areas */}
                    <Card className="border-slate-200 lg:col-span-2">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                                <AlertTriangle className="h-5 w-5 text-amber-500" />
                                Areas Needing Attention
                            </CardTitle>
                            <CardDescription>Subjects with less than 70% accuracy</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {stats?.weak_areas && stats.weak_areas.length > 0 ? (
                                <div className="space-y-3">
                                    {stats.weak_areas.map(subject => {
                                        const subjectStats = stats.by_subject[subject];
                                        const colors = SUBJECT_COLORS[subject];
                                        return (
                                            <div 
                                                key={subject}
                                                className="flex items-center justify-between p-4 rounded-lg bg-slate-50 border border-slate-200"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Badge className={`${colors.bg} ${colors.text} ${colors.border}`}>
                                                        {subject}
                                                    </Badge>
                                                    <span className="text-sm text-slate-600">
                                                        {subjectStats?.total || 0} questions attempted
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-lg font-semibold text-red-600">
                                                        {subjectStats?.accuracy || 0}%
                                                    </span>
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline"
                                                        onClick={() => navigate(`/practice?subject=${subject}`)}
                                                    >
                                                        Practice
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="flex items-center justify-center h-32 text-slate-500">
                                    <div className="text-center">
                                        <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-400" />
                                        <p>Great job! No weak areas identified.</p>
                                        <p className="text-sm">Keep practicing to maintain your performance!</p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Difficulty Distribution */}
                    <Card className="border-slate-200">
                        <CardHeader>
                            <CardTitle style={{ fontFamily: 'Manrope' }}>Difficulty Distribution</CardTitle>
                            <CardDescription>Questions by difficulty level</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {difficultyData.length > 0 ? (
                                <div className="flex flex-col items-center">
                                    <ResponsiveContainer width="100%" height={150}>
                                        <PieChart>
                                            <Pie
                                                data={difficultyData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={40}
                                                outerRadius={60}
                                                paddingAngle={5}
                                                dataKey="value"
                                            >
                                                {difficultyData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="flex gap-4 mt-2">
                                        {difficultyData.map(d => (
                                            <div key={d.name} className="flex items-center gap-2">
                                                <div 
                                                    className="w-3 h-3 rounded-full" 
                                                    style={{ backgroundColor: d.color }}
                                                />
                                                <span className="text-sm text-slate-600">
                                                    {d.name}: {d.value}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="h-[180px] flex items-center justify-center text-slate-500">
                                    <p className="text-sm">No questions attempted yet</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </Layout>
    );
}
